import React, { useState, useEffect, useContext } from "react";
import { Form, Button } from "react-bootstrap";
import axios from "axios";
import useCookie from "../../hooks/useCookie";
import { EntityManagementContext } from "../../context/EntityManagementContext";

// Create new or edit existing student entity
function ManageStudentForm(props) {
  // Value of form elements; used to load data in edit mode
  // and grabbed upon save
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [age, setAge] = useState("");
  const [classIDs, setClassIDs] = useState([]);
  const [success, setSuccess] = useState(false);

  // When set to true, displays an error badge that helps
  // user correct their error.
  const [errorState, setErrorState] = useState(false);

  // List of errors to display after creation/editing error
  // occurs; implemented as dictionary mapping input field
  // to associated list of error messages.
  const [errorDict, setErrorDict] = useState({});

  const csrftoken = useCookie("csrftoken");

  const {
    editingID,
    exit,
    mode,
    setMode,
    classes,
    teachers,
    updateEntities
  } = useContext(EntityManagementContext);

  // Badge that appears upon sucessful creation/edit of class
  const successBadge = () => {
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 5000);
  };

  // If a student has been selected to edit, populate
  // all form options with currently saved data
  useEffect(() => {
    if (mode === "Edit") {
      // Clear any error messages from previous
      // operations.
      setErrorState(false);
      setErrorDict({});

      axios.get("/api/students/" + editingID)
        .then((response) => {
          let editingStudent = response.data;
          setFirstName(editingStudent.first_name);
          setLastName(editingStudent.last_name);
          setEmail(editingStudent.email);
          setAge(editingStudent.age);
          setClassIDs(editingStudent.classes);
        });
    }
  }, [editingID]);

  // Clear all form values
  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setAge("");
    setEmail("");
    setClassIDs([]);
  }

  // Cancel creation or editing of student
  const cancelAction = () => {
    if (mode === "Edit") {
      setMode("Create");
    }
    clearForm();
    exit();
  }

  // When class checkboxes are selected/deselected, update state
  const handleSelect = (event, id) => {
    const isChecked = event.target.checked;

    // If the checkbox has been checked, add the class to the classes list.
    if (isChecked) {
      setClassIDs([...classIDs, id]);
    }
    // If the checkbox has been unchecked, remove the class from the classes list.
    else {
      const filteredIDs = classIDs.filter((find) => find !== id);
      setClassIDs(filteredIDs);
    }
  };

  // Organize validation error information for error badge display.
  //  errorObj: HTTP response carrying error information, as JSON object
  const handleFieldValidationErrors = (errorObj) => {
    // Gather errors, which will we use to update state at the end
    let currentErrorDict = {};

    // Get response body
    let errorBody = errorObj.response.data;

    // This is MVP - just handling errors from server-side validation
    // Would be better if we had client-side validation as well to make 
    // the user registration process faster

    // Handle first name related errors
    if (errorBody["first_name"] && errorBody["first_name"].length >= 1) {
      currentErrorDict["First name"] = errorBody["first_name"];
    }

    // Handle last name related errors
    if (errorBody["last_name"] && errorBody["last_name"].length >= 1) {
      currentErrorDict["Last name"] = errorBody["last_name"];
    }

    // Handle email (username) related errors
    if (errorBody["email"] && errorBody["email"].length >= 1) {
      currentErrorDict["Username"] = errorBody["email"];
    }

    // Handle age related errors
    if (errorBody["age"] && errorBody["age"].length >= 1) {
      currentErrorDict["Age"] = errorBody["age"];
    }

    setErrorDict(currentErrorDict);
    setErrorState(true);
  }

  // Submission of form
  // Depending on current mode, creates or saves entity to database
  const handleSubmit = async (e) => {
    // Prevent default behavior - do not clear inputs immediately
    e.preventDefault();

    // Clear error state
    setErrorState(false);
    setErrorDict({});

    const headers = {
      "X-CSRFToken": csrftoken,
    };

    // Use form values to create new entity in database
    if (mode === "Create") {
      // Create two galleries for the new student
      const gallery1 = await axios.post(
        "/api/galleries/",
        {
          name: "Recent Gallery",
        },
        { headers }
      );
      const gallery2 = await axios.post(
        "/api/galleries/",
        {
          name: "Saved Gallery",
        },
        { headers }
      );
      const newStudent = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        age: age,
        classes: classIDs,
        recent: gallery1.data.galleryID,
        saved: gallery2.data.galleryID,
      };

      axios
        .post("/api/students/", newStudent, { headers })
        .then((response) => {
          if (response.status === 201) {
            successBadge();
            // Clear form values
            clearForm();
            updateEntities(response.data);
          }
        })
        .catch(error => {
          handleFieldValidationErrors(error);
        });
    } else if (mode === "Edit") { // Update existing entity in database
      const updatedStudent = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        age: age,
        classes: classIDs
      };

      axios.patch("/api/students/" + editingID, updatedStudent, { headers })
        .then((response) => {
          if (response.status === 200) {
            successBadge();
            // Clear form values
            clearForm();
            // Set mode back to create
            setMode("Create");
            updateEntities(response.data);
          }
        })
        .catch(error => {
          handleFieldValidationErrors(error);
        });
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group controlId="formBasicFirstName">
        <Form.Label>First Name</Form.Label> {/* Form item: First name */}
        <Form.Control
          type="text"
          placeholder="Enter first name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
      </Form.Group>

      <Form.Group controlId="formBasicLastName">
        <Form.Label>Last Name</Form.Label> {/* Form item: Last name */}
        <Form.Control
          type="text"
          placeholder="Enter last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
      </Form.Group>

      <Form.Group controlId="formBasicEmail">
        <Form.Label>Username</Form.Label> {/* Form item: Email */}
        <Form.Control
          type="text"
          placeholder="Enter username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </Form.Group>

      <Form.Group controlId="formBasicAge">
        <Form.Label>Age</Form.Label> {/* Form item: Age */}
        <Form.Control
          type="number"
          placeholder="Enter age"
          value={age}
          onChange={(e) => setAge(e.target.value)}
          required
        />
      </Form.Group>

      {classes.length > 0 ? (
        <Form.Group controlId="formStudentClass">
          <Form.Label>Enroll in Classes</Form.Label> {/* Form item: Classes (checkboxes) */}
          {classes.map((cls) => {
            return <Form.Check
              type="checkbox"
              key={cls.classID}
              id={cls.classID}
              label={((teacher) => // Teacher first and last name
                        `${cls.className} (${teacher?.first_name} ${teacher?.last_name})`)(teachers.find(teacher => teacher.id == cls.teacher))}
              onChange={(event) => handleSelect(event, cls.classID)}
              checked={classIDs.includes(cls.classID)}
            />
          })}
        </Form.Group>
      ) : null}

      {/* Submit button */}
      <Button id="addStudent" className="admin-button" variant="primary" type="submit">
        {mode} Student
      </Button>

      {/* Cancel button */}
      <Button
        className="admin-button"
        onClick={cancelAction}
        style={{ marginTop: "5px" }}
      >
        Cancel
      </Button>

      {/* Show success badge if action was successful */}
      {success ? (
        <div
          className="alert alert-success"
          role="alert"
          style={{ marginTop: "5px" }}
        >
          Successful!
        </div>
      ) : null}

      {/* If there are any erroneous inputs, let the user know */}
      {errorState ? (
        <div
          className="alert alert-danger"
          role="alert"
          style={{ marginTop: "5px" }}
        >
          <strong>An error occurred.</strong><br /><br />
          {Object.entries(errorDict).map(
            (field) =>
              <>
                <i>{field[0]}</i>
                <ul>
                  {field[1].map((error) =>
                    <li>{error}</li>
                  )}
                </ul>
              </>
          )}
        </div>
      ) : null}
    </Form>
  );
}

export default ManageStudentForm;
